if (!customElements.get('load-more')) {
  customElements.define('load-more', class LoadMore extends HTMLElement {
    constructor() {
      super();

      this.moreLink = this.querySelector('[data-load-more-link]');
      this.previousLink = this.querySelector('[data-load-previous-link]');

      this.moreContainer = this.querySelector('[data-load-more-container]');
      this.previousContainer = this.querySelector('[data-load-previous-container]');

      this.moreSpinner = this.querySelector('[data-load-more-spinner]');
      this.previousSpinner = this.querySelector('[data-load-previous-spinner]');

      this.moreText = this.querySelector('.load-more-text');
      this.previousText = this.querySelector('.load-previous-text');

      this.currentCountEl = this.querySelector('.load-more-current');

      this.isLoading = false;
      this.productClickTrackingInitialised = false;

      this.handleProductClick = this.handleProductClick.bind(this);
      this.handleMoreClick = this.handleMoreClick.bind(this);
      this.handlePreviousClick = this.handlePreviousClick.bind(this);

      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      if (this.moreLink) {
        this.moreLink.addEventListener('click', this.handleMoreClick);
      }

      if (this.previousLink) {
        this.previousLink.addEventListener('click', this.handlePreviousClick);
      }
    }

    connectedCallback() {
      this.restoreState();
      this.setupProductClickTracking();
      this.updateCount();
      this.syncLinks();
    }

    disconnectedCallback() {
      if (this.productClickTrackingInitialised) {
        document.removeEventListener('click', this.handleProductClick);
        this.productClickTrackingInitialised = false;
      }
    }

    get sectionId() {
      return this.getAttribute('data-section-id');
    }

    get nextUrl() {
      return this.getAttribute('data-next-url');
    }

    get previousUrl() {
      return this.getAttribute('data-previous-url');
    }

    get cacheKey() {
      return `${window.location.pathname}${window.location.search}`;
    }

    handleMoreClick(event) {
      event.preventDefault();
      this.loadNextPage();
    }

    handlePreviousClick(event) {
      event.preventDefault();
      this.loadPreviousPage();
    }

    setLoading(loading, direction = null) {
      this.isLoading = loading;

      const loadingNext = loading && direction === 'next';
      const loadingPrevious = loading && direction === 'previous';

      if (this.moreText) {
        this.moreText.classList.toggle('hidden', loadingNext);
      }

      if (this.moreSpinner) {
        this.moreSpinner.classList.toggle('hidden', !loadingNext);
      }

      if (this.previousText) {
        this.previousText.classList.toggle('hidden', loadingPrevious);
      }

      if (this.previousSpinner) {
        this.previousSpinner.classList.toggle('hidden', !loadingPrevious);
      }

      [this.moreLink, this.previousLink].forEach((link) => {
        if (!link) return;

        link.classList.toggle('disabled', loading);

        if (loading) {
          link.setAttribute('aria-disabled', 'true');
        } else {
          link.removeAttribute('aria-disabled');
        }
      });

      if (this.moreLink) {
        this.moreLink.setAttribute('aria-busy', loadingNext ? 'true' : 'false');
      }

      if (this.previousLink) {
        this.previousLink.setAttribute('aria-busy', loadingPrevious ? 'true' : 'false');
      }

      if (!loading) {
        this.syncLinks();
      }
    }

    syncLinks() {
      this.syncLink({
        link: this.moreLink,
        container: this.moreContainer,
        url: this.nextUrl
      });

      this.syncLink({
        link: this.previousLink,
        container: this.previousContainer,
        url: this.previousUrl
      });
    }

    syncLink({ link, container, url }) {
      if (!link) return;

      if (url) {
        link.href = url;
        link.removeAttribute('aria-disabled');
        link.classList.remove('disabled');

        if (container) {
          container.hidden = false;
        }
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.classList.add('disabled');

        if (container) {
          container.hidden = true;
        }
      }
    }

    updateCount() {
      const grid = document.getElementById('product-grid');

      if (grid && this.currentCountEl) {
        this.currentCountEl.textContent = grid.children.length;
      }
    }

    getFetchUrl(url) {
      const fetchUrl = new URL(url, window.location.origin);

      if (this.sectionId) {
        fetchUrl.searchParams.set('section_id', this.sectionId);
      }

      return fetchUrl.toString();
    }

    updateBrowserPage(url) {
      const requestedUrl = new URL(url, window.location.origin);
      const page = requestedUrl.searchParams.get('page');

      if (!page) return;

      const currentUrl = new URL(window.location.href);

      if (page === '1') {
        currentUrl.searchParams.delete('page');
      } else {
        currentUrl.searchParams.set('page', page);
      }

      history.replaceState(
        { ...(history.state || {}), infinitePage: page },
        '',
        currentUrl.toString()
      );
    }

    async fetchPage(url) {
      const response = await fetch(this.getFetchUrl(url), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error(`Collection pagination request failed: ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    }

    async loadNextPage() {
      const url = this.nextUrl;

      if (!url || this.isLoading) return;

      this.setLoading(true, 'next');

      try {
        const html = await this.fetchPage(url);
        const newProducts = html.querySelectorAll('#product-grid > li');
        const pageController = html.querySelector('load-more');
        const productGrid = document.getElementById('product-grid');

        if (!productGrid || newProducts.length === 0) {
          this.removeAttribute('data-next-url');
          this.syncLinks();
          return;
        }

        const fragment = document.createDocumentFragment();

        newProducts.forEach((item) => {
          fragment.appendChild(item);
        });

        productGrid.appendChild(fragment);

        const nextUrl = pageController?.getAttribute('data-next-url');

        if (nextUrl) {
          this.setAttribute('data-next-url', nextUrl);
        } else {
          this.removeAttribute('data-next-url');
        }

        this.updateBrowserPage(url);
        this.updateCount();
        this.syncLinks();

        this.dispatchProductsAdded(newProducts.length, productGrid, 'next');
      } catch (error) {
        console.error('Unable to load more products:', error);
      } finally {
        this.setLoading(false);
      }
    }

    async loadPreviousPage() {
      const url = this.previousUrl;

      if (!url || this.isLoading) return;

      this.setLoading(true, 'previous');

      try {
        const html = await this.fetchPage(url);
        const newProducts = html.querySelectorAll('#product-grid > li');
        const pageController = html.querySelector('load-more');
        const productGrid = document.getElementById('product-grid');

        if (!productGrid || newProducts.length === 0) {
          this.removeAttribute('data-previous-url');
          this.syncLinks();
          return;
        }

        const fragment = document.createDocumentFragment();

        newProducts.forEach((item) => {
          fragment.appendChild(item);
        });

        productGrid.insertBefore(fragment, productGrid.firstChild);

        const previousUrl = pageController?.getAttribute('data-previous-url');

        if (previousUrl) {
          this.setAttribute('data-previous-url', previousUrl);
        } else {
          this.removeAttribute('data-previous-url');
        }

        this.updateBrowserPage(url);
        this.updateCount();
        this.syncLinks();

        this.dispatchProductsAdded(newProducts.length, productGrid, 'previous');
      } catch (error) {
        console.error('Unable to load previous products:', error);
      } finally {
        this.setLoading(false);
      }
    }

    dispatchProductsAdded(count, grid, direction) {
      document.dispatchEvent(new CustomEvent('collection:products-appended', {
        detail: {
          count,
          grid,
          direction
        }
      }));
    }

    setupProductClickTracking() {
      if (this.productClickTrackingInitialised) return;

      document.addEventListener('click', this.handleProductClick);
      this.productClickTrackingInitialised = true;
    }

    handleProductClick(event) {
      const productLink = event.target.closest('#product-grid a[href*="/products/"]');

      if (!productLink) return;

      this.saveState();
    }

    saveState() {
      const grid = document.getElementById('product-grid');

      if (!grid) return;

      const key = this.cacheKey;

      sessionStorage.setItem(
        `infinite_grid_${key}`,
        grid.innerHTML
      );
      sessionStorage.setItem(
        `infinite_scroll_${key}`,
        String(window.scrollY)
      );
      sessionStorage.setItem(
        `infinite_next_${key}`,
        this.nextUrl || ''
      );
      sessionStorage.setItem(
        `infinite_previous_${key}`,
        this.previousUrl || ''
      );
    }

    restoreState() {
      const key = this.cacheKey;
      const gridHtml = sessionStorage.getItem(`infinite_grid_${key}`);
      const scrollY = sessionStorage.getItem(`infinite_scroll_${key}`);
      const nextUrl = sessionStorage.getItem(`infinite_next_${key}`);
      const previousUrl = sessionStorage.getItem(`infinite_previous_${key}`);

      if (!gridHtml) return false;

      const grid = document.getElementById('product-grid');

      if (grid) {
        grid.innerHTML = gridHtml;
      }

      if (nextUrl) {
        this.setAttribute('data-next-url', nextUrl);
      } else {
        this.removeAttribute('data-next-url');
      }

      if (previousUrl) {
        this.setAttribute('data-previous-url', previousUrl);
      } else {
        this.removeAttribute('data-previous-url');
      }

      this.syncLinks();
      this.updateCount();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollY !== null) {
            window.scrollTo({
              top: Number.parseInt(scrollY, 10) || 0,
              behavior: 'auto'
            });
          }
        });
      });

      sessionStorage.removeItem(`infinite_grid_${key}`);
      sessionStorage.removeItem(`infinite_scroll_${key}`);
      sessionStorage.removeItem(`infinite_next_${key}`);
      sessionStorage.removeItem(`infinite_previous_${key}`);

      return true;
    }
  });
}
